import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance";
import appConfigs from "@configs/app";
import { googleDrive } from "@plugins/google/google.drive";
import { FILE_TYPE } from "@plugins/google/google.constant";

export function useGoogleCommon() {
  const { t } = useI18n();
  const modal = useModal();

  const showAppAvailabilityWarningModal = () => {
    return new Promise<void>((resolve, reject) => {
      const props = {
        title: t("google_app_availability_warning_title"),
        content: t("google_app_availability_warning_content"),
        primaryBtnLabel: t("button_confirm"),
        secondaryBtnLabel: t("button_cancel"),
        actionPrimary: () => {
          resolve();
        },
        actionHidden: () => {
          reject();
        },
        actionHideRequest: () => {
          reject();
        },
      };
      modal.showMessage("warning", props);
    });
  };

  const showAppAvailabilityErrorModal = () => {
    return new Promise<void>((resolve, reject) => {
      const props = {
        title: t("google_app_availability_error_title"),
        content: t("google_app_availability_error_content"),
        primaryBtnLabel: t("button_confirm"),
        use_secondaryButton: false,
        actionPrimary: () => {
          reject();
        },
      };
      modal.showMessage("warning", props);
    });
  };

  const openNotLinkedModal = (action: string) => {
    const titleMap: Record<string, string> = {
      download: t("tinymce_plugin_not_linked_download_modal_title"),
      remove: t("tinymce_plugin_not_linked_remove_modal_title"),
      pdfMark: t("tinymce_plugin_not_linked_mark_as_pdf_modal_title"),
      googleMark: t("tinymce_plugin_not_linked_mark_as_google_modal_title"),
      googleMeet: t("meeting_link_account_not_linked_modal_title"),
    };

    const title = titleMap[action] || t("tinymce_plugin_not_linked_open_in_new_tab_modal_title");

    const props = {
      title,
      content: t("tinymce_plugin_not_linked_modal_message", {
        linkAccountUrl: appConfigs.vleDomain + `/profile/linked-account`,
      }),
      primaryBtnLabel: t("button_ok"),
      use_secondaryButton: false,
    };
    modal.showMessage("warning", props);
  };

  const checkUserEditPermission = async (documentId: string): Promise<[boolean, string]> => {
    let isAbleToEdit = true;
    let mimeType = "";
    try {
      const response = await googleDrive.getUserDriveFileData(documentId);
      if (!response?.capabilities?.canEdit) {
        isAbleToEdit = false;
        mimeType = response?.mimeType ?? "";
      }
    } catch {
      isAbleToEdit = false;
    }
    return [isAbleToEdit, mimeType];
  };

  const openGrantEditPermissionModal = (docId: string, mimeType: string) => {
    let openInNewTabLink = "";
    switch (mimeType) {
      case FILE_TYPE.DOCUMENT.VALUE:
        openInNewTabLink = `https://docs.google.com/document/d/${docId}/`;
        break;
      case FILE_TYPE.SPREADSHEET.VALUE:
        openInNewTabLink = `https://docs.google.com/spreadsheets/d/${docId}/`;
        break;
      case FILE_TYPE.PRESENTATION.VALUE:
        openInNewTabLink = `https://docs.google.com/presentation/d/${docId}/`;
        break;
    }
    modal.showMessage(
      "warning",
      {
        title: t("tinymce_plugin_not_edit_permission_modal_title"),
        content: t("tinymce_plugin_not_edit_permission_modal_message"),
        primaryBtnLabel: t("button_go_to_file"),
        secondaryBtnLabel: t("button_cancel"),
        actionPrimary: () => {
          if (openInNewTabLink) window.open(openInNewTabLink, "_blank");
        },
      },
      true,
    );
  };

  return {
    showAppAvailabilityWarningModal,
    showAppAvailabilityErrorModal,
    openNotLinkedModal,
    checkUserEditPermission,
    openGrantEditPermissionModal,
  };
}
